import { flash } from "@xola/ui-kit";
import { omit } from "lodash";
import React, { ReactNode, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { Media, MediaSize, MediaType, PreferenceContext, PreferenceList } from "types";
import { MediaUpload } from "../Settings/Kiosk/MediaUpload";
import { api } from "@/api/api";
import { uploadFile } from "@/api/mediaApi";
import { xolaUri } from "@/api/xolaUri";
import { getSeller } from "@/utils/getSeller";

interface MediaUploadInputProps {
    uploadUrl: string;
    source?: string;
    filename?: string;
    size?: MediaSize;
    mediaType?: MediaType;
    allowedExtensions?: string[] | null;
    makeThumbnail: boolean;
    maxSize?: number;
    requirements?: ReactNode | null;
    hasDelete?: boolean;
    returnResponse?: boolean;
    onDelete?: () => void;
    onUpdated?: (media?: Media) => void;
    onError: (error: string) => void;
    setIsUploading?: (isUploading: boolean) => void;
}

type PreferenceGeneric = {
    id?: ID;
    key: string;
    value: unknown;
    seller: { id: ID };
    context: PreferenceContext;
};

const thumbnailPreferenceKey = "xola.seller.media.meta";

/**
 * Helper to create a thumbnail from an image file.
 * Returns a Promise<File> of the thumbnail image (JPEG, 200x200px).
 */
async function createImageThumbnail(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
        try {
            const img = new window.Image();
            const url = URL.createObjectURL(file);
            img.addEventListener("load", () => {
                try {
                    const canvas = document.createElement("canvas");
                    const size = 200;
                    // maintain aspect ratio, fit within 200x200
                    let w = img.width,
                        h = img.height;
                    if (w > h) {
                        if (w > size) {
                            h = Math.round(h * (size / w));
                            w = size;
                        }
                    } else {
                        if (h > size) {
                            w = Math.round(w * (size / h));
                            h = size;
                        }
                    }
                    canvas.width = w;
                    canvas.height = h;
                    const context = canvas.getContext("2d");
                    if (!context) {
                        console.error("[createImageThumbnail] Could not get 2D context from canvas");
                        URL.revokeObjectURL(url);
                        reject(new Error("Could not get 2D context from canvas"));
                        return;
                    }
                    context.drawImage(img, 0, 0, w, h);
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                console.error("[createImageThumbnail] Failed to create thumbnail blob");
                                URL.revokeObjectURL(url);
                                reject(new Error("Failed to create thumbnail blob"));
                                return;
                            }
                            URL.revokeObjectURL(url);
                            resolve(new File([blob], "thumbnail.jpg", { type: "image/jpeg" }));
                        },
                        "image/jpeg",
                        0.8,
                    );
                } catch (error) {
                    console.error("[createImageThumbnail] Error during canvas processing", error);
                    URL.revokeObjectURL(url);
                    reject(error);
                }
            });
            img.addEventListener("error", (e) => {
                console.error("[createImageThumbnail] Image failed to load", e);
                URL.revokeObjectURL(url);
                reject(e);
            });
            img.src = url;
        } catch (error) {
            console.error("[createImageThumbnail] Error during image thumbnail creation", error);
            reject(error);
        }
    });
}

/**
 * Helper to create a thumbnail from a video file.
 * Returns a Promise<File> of the thumbnail image (JPEG, 200x200px).
 */
async function createVideoThumbnail(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
        try {
            const video = document.createElement("video");
            video.preload = "metadata";
            video.muted = true;
            video.playsInline = true;
            const url = URL.createObjectURL(file);

            video.addEventListener("loadeddata", () => {
                try {
                    // Seek to 1s or 0s if shorter
                    video.currentTime = Math.min(1, video.duration || 0);
                } catch (error) {
                    console.error("[createVideoThumbnail] Error during video seek", error);
                    URL.revokeObjectURL(url);
                    reject(error);
                }
            });

            video.addEventListener("seeked", () => {
                try {
                    const canvas = document.createElement("canvas");
                    const size = 200;
                    let w = video.videoWidth,
                        h = video.videoHeight;
                    if (w > h) {
                        if (w > size) {
                            h = Math.round(h * (size / w));
                            w = size;
                        }
                    } else {
                        if (h > size) {
                            w = Math.round(w * (size / h));
                            h = size;
                        }
                    }
                    canvas.width = w;
                    canvas.height = h;
                    const context = canvas.getContext("2d");
                    if (!context) {
                        console.error("[createVideoThumbnail] Could not get 2D context from canvas");
                        URL.revokeObjectURL(url);
                        reject(new Error("Could not get 2D context from canvas"));
                        return;
                    }
                    context.drawImage(video, 0, 0, w, h);
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                console.error("[createVideoThumbnail] Failed to create thumbnail blob");
                                URL.revokeObjectURL(url);
                                reject(new Error("Failed to create thumbnail blob"));
                                return;
                            }
                            URL.revokeObjectURL(url);
                            resolve(new File([blob], "thumbnail.jpg", { type: "image/jpeg" }));
                        },
                        "image/jpeg",
                        0.8,
                    );
                } catch (error) {
                    console.error("[createVideoThumbnail] Error during canvas processing", error);
                    URL.revokeObjectURL(url);
                    reject(error);
                }
            });

            video.addEventListener("error", (e) => {
                console.error("[createVideoThumbnail] Video failed to load", e);
                URL.revokeObjectURL(url);
                reject(e);
            });
            video.src = url;
        } catch (error) {
            console.error("[createVideoThumbnail] Error during video thumbnail creation", error);
            reject(error);
        }
    });
}

/**
 * Helper to fetch all seller preferences for a given sellerId.
 * Returns the preferences array.
 */
async function fetchPreferences() {
    const { data } = await api.get<PreferenceList>(`/preferences?context=seller`);
    return data.data;
}

/**
 * Helper to identify thumbnail preferences
 */
function isThumbMap(v: unknown): v is Record<string, { thumbnail: ID }> {
    if (typeof v !== "object" || v === null) return false;
    for (const value of Object.values(v as Record<string, unknown>)) {
        if (typeof value !== "object" || value === null || typeof (value as any).thumbnail !== "string") {
            return false;
        }
    }
    return true;
}

/**
 * Helper to fetch and match media and thumbnail for a given filename.
 * Returns { mediaSource, thumbnailSource }
 */
async function getMatchedMedia(filename: string): Promise<{ mediaSource?: string; thumbnailSource?: string }> {
    if (!filename) {
        return { mediaSource: undefined, thumbnailSource: undefined };
    }

    const sellerId = getSeller().id;

    // 1. Fetch all seller media
    const { data: mediaList } = await api.get<Media[]>(`/sellers/${sellerId}/medias`);

    // 2. Find the media object whose src matches the filename
    let matchedMedia = null;
    for (const media of mediaList) {
        if (media.src && (media.src === filename || filename.endsWith(media.src) || media.src.endsWith(filename))) {
            matchedMedia = media;
            break;
        }
    }

    if (!matchedMedia) {
        return { mediaSource: undefined, thumbnailSource: undefined };
    }

    const mediaSource = `${xolaUri}${matchedMedia.src}`;
    const mediaUUID = matchedMedia.id;

    // 3. Fetch all seller preferences using the shared helper
    const prefs = (await fetchPreferences()) as PreferenceGeneric[];
    const thumbPref = prefs.find((p: any) => p.key === thumbnailPreferenceKey && p.context === "seller");
    const thumbPrefValue = thumbPref?.value;
    if (!isThumbMap(thumbPrefValue)) {
        return { mediaSource, thumbnailSource: undefined };
    }

    //const thumbnailId = v[mediaUUID]?.thumbnail; // OK: v is now the right type
    // 4. Look up the thumbnail ID for the matched media

    const thumbnailId = thumbPrefValue[mediaUUID]?.thumbnail;
    // 5. Find the thumbnail media object by ID in the media list
    const thumbnailMedia = mediaList.find((m: any) => m.id === thumbnailId);
    if (thumbnailMedia?.src) {
        // 6. Set thumbnailSrc (prepend xolaUri if needed)
        const thumbnailUrl = thumbnailMedia.src.startsWith("http")
            ? thumbnailMedia.src
            : `${xolaUri}${thumbnailMedia.src}`;
        return { mediaSource, thumbnailSource: thumbnailUrl };
    }
    //}

    return { mediaSource, thumbnailSource: undefined };
}

/**
 * Helper to create a new seller preference.
 */
async function createPreference(sellerId: string, key: string, value: any) {
    return api.post("/preferences", {
        context: "seller",
        seller: { id: sellerId },
        key,
        value,
    });
}

// Helper to ensure valid file extension (UI error handling should be done by the caller)
function ensureValidFileExtension(file: File, mediaType: string): void {
    if (!file?.size) return;

    const isVideo = mediaType === "video";
    const validVideoExtensions = /\.(mp4|mkv|mov)$/i;
    const validImageExtensions = /\.(jpg|jpeg|png)$/i;

    if ((isVideo && !validVideoExtensions.test(file.name)) || (!isVideo && !validImageExtensions.test(file.name))) {
        throw new Error("Invalid file extension");
    }
}

// Helper to get media id from media object
function getMediaId(media: any): string | undefined {
    return media?.id || media?.data?.id;
}

// Helper to process thumbnail URL
function processThumbnailUrl(url?: string): string | undefined {
    return url ? (url.startsWith("http") ? url : `${xolaUri}${url}`) : undefined;
}

// Update an existing preference by id
async function updatePreference(id: string, value: any) {
    const returnValueValue = await api.patch(`/preferences/${id}`, { value });
    return returnValueValue;
}

// Remove duplicate preferences for the key
async function deletePreference(sellerId: string, id: string) {
    return await api.delete(`/preferences/${id}?context=seller`);
}

async function upsertPreference(sellerId: string, existingPref: any, mediaId: string, thumbnailId: string) {
    const newValue = existingPref
        ? { ...existingPref.value, [mediaId]: { thumbnail: thumbnailId } }
        : { [mediaId]: { thumbnail: thumbnailId } };

    await (existingPref?.id
        ? updatePreference(existingPref.id, newValue)
        : createPreference(sellerId, thumbnailPreferenceKey, newValue));
}

export const MediaUploadInput = ({
    source = "",
    onUpdated,
    uploadUrl,
    onError,
    filename = "",
    makeThumbnail = false,
    allowedExtensions,
    mediaType = "image",
    size = "large",
    maxSize,
    requirements,
    hasDelete,
    onDelete,
    returnResponse = false,
    setIsUploading,
}: MediaUploadInputProps) => {
    const [thumbnailSource, setThumbnailSource] = useState<string | undefined>(undefined);
    const [mediaSource, setMediaSource] = useState<string | undefined>(source);
    const sellerId = getSeller().id;

    useEffect(() => {
        let isMounted = true;
        async function fetchAndSetThumbnail() {
            if (!filename) {
                setThumbnailSource(undefined);
                setMediaSource(undefined);
                return;
            }

            try {
                const { mediaSource, thumbnailSource } = await getMatchedMedia(filename);
                if (isMounted) {
                    setMediaSource(mediaSource);
                    setThumbnailSource(thumbnailSource);
                }
            } catch (error) {
                console.error("[MediaUploadInput][useEffect] Failed to fetch thumbnail preference", error);
                if (isMounted) {
                    setThumbnailSource(undefined);
                }
            }
        }
        fetchAndSetThumbnail();
        return () => {
            isMounted = false;
        };
    }, [filename]);

    const { mutateAsync: updateFile, isLoading } = useMutation(async (file: File) => {
        // Use axios and not api here because stringifyDates middleware breaks binary uploads
        const response = await uploadFile(uploadUrl, file);
        return returnResponse ? response : response.data.media;
    });

    const uploadThumbnail = async (thumbnailFile: File) => {
        return uploadFile(uploadUrl, thumbnailFile);
    };

    if (setIsUploading) {
        setIsUploading(isLoading);
    }

    const updateMediaFile = async (mediaFile: File, errorMessage: string) => {
        try {
            try {
                ensureValidFileExtension(mediaFile, mediaType);
            } catch (error) {
                const isVideo = mediaType === "video";
                const allowed = isVideo ? "mp4, mov, mkv" : "jpg, jpeg, png";
                flash.danger(`${mediaFile.name} has an invalid extension. Only ${allowed} are allowed`);
                throw error;
            }
            const media = await updateFile(mediaFile);
            const uploadedMediaId = getMediaId(media);

            if (makeThumbnail) {
                await handleThumbnailCreation(mediaFile, uploadedMediaId);
            } else if (media?.src) {
                setThumbnailSource(`${xolaUri}${media.src}`);
            }

            onUpdated?.(media);
        } catch (error) {
            handleUpdateError(error, errorMessage);
        }
    };

    // --- Helper Functions --- //

    const handleThumbnailCreation = async (mediaFile: File, mediaId?: string) => {
        try {
            const thumbnailFile = await createThumbnailFile(mediaFile);
            if (!thumbnailFile) {
                console.warn("[MediaUploadInput] Thumbnail file was not created");
                return;
            }

            const { thumbnailId, thumbnailUrl } = await uploadAndProcessThumbnail(thumbnailFile);
            if (mediaId && thumbnailId) {
                await updateThumbnailPreference(mediaId, thumbnailId);
            }

            if (thumbnailUrl) {
                setThumbnailSource(thumbnailUrl);
            }
        } catch (error) {
            console.error("[MediaUploadInput] Failed to create or upload thumbnail", error);
            flash.warning("Failed to create or upload thumbnail.");
        }
    };

    const createThumbnailFile = async (mediaFile: File): Promise<File | null> => {
        return mediaType === "image"
            ? await createImageThumbnail(mediaFile)
            : mediaType === "video"
            ? await createVideoThumbnail(mediaFile)
            : null;
    };

    const uploadAndProcessThumbnail = async (thumbnailFile: File) => {
        const thumbResponse = await uploadThumbnail(thumbnailFile);
        const thumbnailId = thumbResponse?.data?.id;
        const thumbnailUrl = processThumbnailUrl(thumbResponse?.data?.src);
        return { thumbnailId, thumbnailUrl };
    };

    const updateThumbnailPreference = async (mediaId: string, thumbnailId: string) => {
        const prefs = await fetchPreferences();
        const matches = prefs.filter((p: any) => p.key === thumbnailPreferenceKey && p.context === "seller");

        await cleanDuplicatePreferences(matches);
        await upsertPreference(sellerId, matches[0], mediaId, thumbnailId);
    };

    const cleanDuplicatePreferences = async (matches: any[]) => {
        if (matches.length <= 1) return;

        for (let index = 1; index < matches.length; index++) {
            const preferenceId = matches[index]?.id;
            if (preferenceId) await deletePreference(sellerId, preferenceId);
        }
    };

    const handleUpdateError = (error: unknown, defaultMessage: string) => {
        console.error("[MediaUploadInput] Error in updateMediaFile", error);
        if (onError) {
            onError(defaultMessage);
        } else {
            flash.danger(defaultMessage);
        }
    };

    const handleFileChange = (mediaFile: File) => {
        updateMediaFile(mediaFile, "The media failed to upload.");
    };

    const handleDeleteFile = async () => {
        if (!hasDelete || !onDelete) {
            return;
        }

        try {
            // Fetch all seller preferences
            const prefs = await fetchPreferences();
            const matches = prefs.filter((p: any) => p.key === thumbnailPreferenceKey && p.context === "seller");
            let mediaUUID: string | undefined;
            let thumbnailId: string | undefined;

            if (matches.length > 0 && filename) {
                // Find the media object whose src matches the filename
                const { data: mediaList } = await api.get<Media[]>(`/sellers/${sellerId}/medias`);
                let matchedMedia = null;
                for (const media of mediaList) {
                    if (!media.src) continue;
                    if (media.src === filename || filename.endsWith(media.src) || media.src.endsWith(filename)) {
                        matchedMedia = media;
                        break;
                    }
                }
                if (matchedMedia) {
                    mediaUUID = matchedMedia.id;
                    // Try to get the thumbnailId from the preference
                    for (const pref of matches) {
                        if (!mediaUUID) break;
                        if (!isThumbMap(pref.value)) continue;
                        const entry = pref.value[mediaUUID];
                        if (!entry?.thumbnail) continue;
                        if (pref.id) {
                            const nextValue = omit(pref.value, [mediaUUID]);
                            await api.patch(`/preferences/${pref.id}`, { value: nextValue });
                        }
                        break; // stop after first successful hit
                    }
                }
            }

            // Delete the main media file
            if (mediaUUID) {
                try {
                    await api.delete(`/sellers/${sellerId}/medias/${mediaUUID}`);
                } catch (error) {
                    console.error("[MediaUploadInput] Failed to delete main media file", error);
                }
            }

            // Delete the thumbnail media file
            if (thumbnailId) {
                try {
                    await api.delete(`/sellers/${sellerId}/medias/${thumbnailId}`);
                } catch (error) {
                    console.error("[MediaUploadInput] Failed to delete thumbnail media file", error);
                }
            }
        } catch (error) {
            console.error("[MediaUploadInput] Failed to delete associated metadata or media files", error);
        }
        onDelete();
    };

    return (
        <MediaUpload
            size={size}
            source={mediaSource}
            thumbnailSrc={thumbnailSource}
            isLoading={isLoading}
            mediaType={mediaType}
            maxSize={maxSize}
            allowedExtensions={allowedExtensions ?? undefined}
            requirements={requirements ?? undefined}
            hasDelete={hasDelete}
            onChange={handleFileChange}
            onDelete={handleDeleteFile}
            onError={onError}
        />
    );
};
